import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, ImageIcon, Loader2, Sparkles, Check, ChevronRight, Package, Truck, MapPin, Info, Plus, Sprout, Trash2, Scale, WifiOff, Minus, Archive, X } from "lucide-react";
import type { MadeToOrderField } from "@shared/schema";
import { trackEvent } from "@/lib/analytics";
import {
  beginComposer,
  getDraft,
  removeDraft,
  useComposerDrafts,
  type ComposerAnalysis,
} from "@/lib/listingComposer";
import { Button } from "@/components/ui/button";
import { HeatPicker } from "@/components/HeatPicker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CLOTHING_CATEGORY,
  GARMENT_TYPES,
  CLOTHING_SIZES,
  CLOTHING_CONDITIONS,
  CLOTHING_VIBES,
} from "@shared/clothing";
import { useToast } from "@/hooks/use-toast";
import { SellerAddressFixer, hasGeocodedAddress } from "@/components/SellerAddressFixer";

type ProductAnalysis = {
  name: string;
  category: string;
  subtype: string | null;
  description: string;
  estimatedSize: string | null;
  estimatedWeightOz: number | null;
  estimatedWeightLabel: string | null;
  suggestedPriceMin: number | null;
  suggestedPriceMax: number | null;
  unit: string;
  tags: string[];
  confidence: "high" | "medium" | "low";
  pricingBasis: string | null;
  growthState: "ripe" | "growing";
  daysUntilRipe: number | null;
  ripenessNotes: string | null;
  isWholeAnimal?: boolean;
};

const CATEGORIES = [
  { value: "produce", label: "Produce" },
  { value: "eggs", label: "Eggs & Dairy" },
  { value: "baked", label: "Baked Goods" },
  { value: "prepared", label: "Prepared Foods" },
  { value: "meat", label: "Meat & Poultry" },
  { value: "pantry", label: "Pantry" },
  { value: "flowers", label: "Flowers" },
  { value: "homemade", label: "Homemade / Crafted" },
  { value: "garden", label: "Garden" },
  { value: "other", label: "Other" },
];

// Capitalized to match what the dashboard's fulfillment editor writes — the
// column is a plain varchar, not the lowercase shared enum, so the two writers
// have to agree by convention or the same day reads as two different values.
const QUICK_DAYS = [
  { label: "Su", value: "Sunday" },
  { label: "Mo", value: "Monday" },
  { label: "Tu", value: "Tuesday" },
  { label: "We", value: "Wednesday" },
  { label: "Th", value: "Thursday" },
  { label: "Fr", value: "Friday" },
  { label: "Sa", value: "Saturday" },
] as const;

const SHARE_SIZES = [
  { value: "whole", label: "Whole animal", unit: "per animal" },
  { value: "half", label: "Half", unit: "per half share" },
  { value: "quarter", label: "Quarter", unit: "per quarter share" },
  { value: "eighth", label: "Eighth", unit: "per eighth share" },
];
const shareUnit = (size: string) =>
  SHARE_SIZES.find((s) => s.value === size)?.unit ?? "per share";

type Props = {
  // Id of an in-progress listing to reopen from the tray. Undefined starts a
  // fresh one at the photo step.
  openDraftId?: string | null;
  onComplete: (created?: any) => void;
  onCancel: () => void;
  onSwitchToWeightBatch?: (seed: {
    name: string;
    category: string;
    description: string;
    imageUrls: string[];
  }) => void;
};

type FulfillmentProfile = {
  id: string;
  name: string;
  fulfillmentMethod: string;
  isActive: number;
  pickupWindowLabel?: string | null;
};

const METHOD_ICONS: Record<string, typeof Package> = {
  seller_pickup: Package,
  local_delivery: Truck,
  drop_zone_pickup: MapPin,
};

const METHOD_LABELS: Record<string, string> = {
  seller_pickup: "Seller Pickup",
  local_delivery: "Local Delivery",
  drop_zone_pickup: "Hub Drop-off",
};

export function QuickAddListing({ openDraftId, onComplete, onCancel, onSwitchToWeightBatch }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [handingOff, setHandingOff] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const { data: fulfillmentProfiles = [] } = useQuery<FulfillmentProfile[]>({
    queryKey: ["/api/seller/fulfillment-profiles"],
    queryFn: async () => {
      const res = await fetch("/api/seller/fulfillment-profiles", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: account, isLoading: accountLoading } = useQuery<{
    latitude?: string | number | null;
    longitude?: string | number | null;
    glutenFreeMaker?: number | null;
    glutenFreeAllProducts?: number | null;
  }>({
    queryKey: ["/api/account"],
    queryFn: async () => {
      const res = await fetch("/api/account", { credentials: "include" });
      if (!res.ok) throw new Error("Could not load your account");
      return res.json();
    },
  });

  const activeProfiles = fulfillmentProfiles.filter((p) => p.isActive);

  // How this item can be picked up or delivered. Defaults to every option the
  // seller already runs, which is what they use on almost every listing — and
  // matches what the server would have auto-applied anyway. Untouched until the
  // profiles load, so the default isn't computed from an empty list.
  const [selectedFulfillmentIds, setSelectedFulfillmentIds] = useState<string[] | null>(null);
  const [addingFulfillment, setAddingFulfillment] = useState(false);
  const [newFulfillmentName, setNewFulfillmentName] = useState("");
  const [newFulfillmentMethod, setNewFulfillmentMethod] = useState<
    "seller_pickup" | "local_delivery" | "drop_zone_pickup"
  >("seller_pickup");
  const [newFulfillmentZoneId, setNewFulfillmentZoneId] = useState("");
  // Optional window, matching the dashboard's "anytime vs specific" behaviour:
  // no days picked means no time slots, i.e. anytime.
  const [newFulfillmentDays, setNewFulfillmentDays] = useState<string[]>([]);
  const [newFulfillmentStart, setNewFulfillmentStart] = useState("09:00");
  const [newFulfillmentEnd, setNewFulfillmentEnd] = useState("12:00");

  // Only fetched once the seller opens the creator and picks Hub, so the
  // listing flow doesn't pay for a request nobody asked for.
  const { data: dropZones = [] } = useQuery<{ id: string; name: string; city: string | null }[]>({
    queryKey: ["/api/drop-zones"],
    queryFn: async () => {
      const res = await fetch("/api/drop-zones", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: addingFulfillment && newFulfillmentMethod === "drop_zone_pickup",
  });

  // Create an option without leaving the listing, including hub pickup —
  // choosing the hub is just picking from the existing list, which fits here
  // fine. Creating a brand new hub still belongs in the dashboard.
  const createFulfillment = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/seller/fulfillment-profiles", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newFulfillmentName.trim(),
          fulfillmentMethod: newFulfillmentMethod,
          movedBy: newFulfillmentMethod === "local_delivery" ? "seller" : "buyer",
          dropZoneId: newFulfillmentMethod === "drop_zone_pickup" ? newFulfillmentZoneId : undefined,
          feeType: "none",
          isActive: 1,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not add that option");
      const profile = await res.json();

      // One slot per chosen day. Best-effort: the option itself already exists
      // and works as anytime, so a failed slot shouldn't fail the whole add.
      for (const day of newFulfillmentDays) {
        try {
          await fetch(`/api/seller/fulfillment-profiles/${profile.id}/time-slots`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              dayOfWeek: day,
              startTime: newFulfillmentStart,
              endTime: newFulfillmentEnd,
            }),
          });
        } catch {
          /* option stands without this window */
        }
      }
      return profile;
    },
    onSuccess: (created: { id: string }) => {
      // Tick the new option on, since adding one mid-listing means you want it.
      setSelectedFulfillmentIds([...chosenFulfillmentIds, created.id]);
      setNewFulfillmentName("");
      setNewFulfillmentZoneId("");
      setNewFulfillmentDays([]);
      setAddingFulfillment(false);
      queryClient.invalidateQueries({ queryKey: ["/api/seller/fulfillment-profiles"] });
      toast({ title: "Option added" });
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't add that option", description: err.message, variant: "destructive" }),
  });
  useEffect(() => {
    if (selectedFulfillmentIds == null && activeProfiles.length > 0) {
      setSelectedFulfillmentIds(activeProfiles.map((p) => p.id));
    }
  }, [activeProfiles.length, selectedFulfillmentIds]);
  const chosenFulfillmentIds = selectedFulfillmentIds ?? activeProfiles.map((p) => p.id);

  const [step, setStep] = useState<"address" | "photo" | "review" | "clothing_review" | "saving">("photo");
  const [addrError, setAddrError] = useState<string | null>(null);

  useEffect(() => {
    if (accountLoading || !account) return;
    if (!hasGeocodedAddress(account)) {
      setStep((current) => (current === "saving" ? current : "address"));
    }
  }, [accountLoading, account]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [allImageBase64, setAllImageBase64] = useState<string[]>([]);
  // Clothing review (in-dialog, no hand-off). Photos are uploaded up front so
  // the gallery + vision draft use the URLs.
  const [clothingUrls, setClothingUrls] = useState<string[]>([]);
  const [clGarment, setClGarment] = useState("");
  const [clSize, setClSize] = useState("");
  const [clCondition, setClCondition] = useState("reworked");
  const [clColor, setClColor] = useState("");
  const [clTags, setClTags] = useState<string[]>([]);
  const [addingClothingPhoto, setAddingClothingPhoto] = useState(false);
  const clothingPhotoInputRef = useRef<HTMLInputElement>(null);
  const clothingCameraInputRef = useRef<HTMLInputElement>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<ProductAnalysis | null>(null);

  // Editable form fields (filled by AI, adjustable by user)
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("each");
  const [quantity, setQuantity] = useState("10");
  // Extra named price options ("Per dozen" $20 x10). Empty = ordinary
  // single-price listing and buyers see no size picker.
  const [priceOptions, setPriceOptions] = useState<
    { label: string; price: string; qty: string }[]
  >([]);
  const [weight, setWeight] = useState("");
  const [growthState, setGrowthState] = useState<"ripe" | "growing">("ripe");
  const [daysUntilRipe, setDaysUntilRipe] = useState("");
  const [isMadeToOrder, setIsMadeToOrder] = useState(false);
  const [leadTimeDays, setLeadTimeDays] = useState("3");
  const [minQty, setMinQty] = useState("1");
  const [requiresDeposit, setRequiresDeposit] = useState(false);
  const [depositPercent, setDepositPercent] = useState("25");
  const [customFields, setCustomFields] = useState<MadeToOrderField[]>([]);
  // Per-listing gluten-free answer, only asked when the seller is a gluten-free
  // maker in "choose per product" mode. null = unanswered.
  const [glutenFree, setGlutenFree] = useState<boolean | null>(null);
  const [heatLevel, setHeatLevel] = useState<number | null>(null);
  const [isMeatShare, setIsMeatShare] = useState(false);
  const [bulkDiscountOn, setBulkDiscountOn] = useState(false);
  const [discountThreshold, setDiscountThreshold] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [shareSize, setShareSize] = useState("quarter");
  const [processingFee, setProcessingFee] = useState("");
  const [slaughterDate, setSlaughterDate] = useState("");
  const glutenFreePerItem =
    Number(account?.glutenFreeMaker) === 1 && Number(account?.glutenFreeAllProducts) !== 1;

  const newFieldId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `f${Date.now()}${Math.round(Math.random() * 1e6)}`;
  const addField = () =>
    setCustomFields((prev) => [
      ...prev,
      { id: newFieldId(), label: "", type: "text", required: false },
    ]);
  const patchField = (id: string, patch: Partial<MadeToOrderField>) =>
    setCustomFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const removeField = (id: string) =>
    setCustomFields((prev) => prev.filter((f) => f.id !== id));
  const addOption = (id: string) =>
    patchFieldOptions(id, (opts) => [...opts, ""]);
  const patchOption = (id: string, idx: number, val: string) =>
    patchFieldOptions(id, (opts) => opts.map((o, k) => (k === idx ? val : o)));
  const removeOption = (id: string, idx: number) =>
    patchFieldOptions(id, (opts) => opts.filter((_, k) => k !== idx));
  const patchFieldOptions = (id: string, fn: (opts: string[]) => string[]) =>
    setCustomFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, options: fn(f.options ?? []) } : f)),
    );
  const fieldNeedsOptions = (t: MadeToOrderField["type"]) => t === "select" || t === "checkbox";

  // Listing funnel: opened → (ai_failed?) → published / failed / abandoned.
  // The abandon event captures the step the maker was on when they left
  // without publishing, so the open-but-never-publish gap becomes actionable
  // ("bailed at photo" vs "bailed at review").
  const stepRef = useRef(step);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  const publishedRef = useRef(false);
  useEffect(() => {
    trackEvent("list_opened");
    return () => {
      if (!publishedRef.current) {
        trackEvent("list_abandoned", undefined, { step: stepRef.current });
      }
    };
  }, []);

  const uploadBase64 = async (b64: string): Promise<string> => {
    const byteString = atob(b64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const blob = new Blob([bytes], { type: "image/jpeg" });
    const formData = new FormData();
    formData.append("image", blob, "listing.jpg");
    const uploadRes = await fetch("/api/uploads/item-image", {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (!uploadRes.ok) throw new Error("upload failed");
    return (await uploadRes.json()).imageUrl || "";
  };

  // Apparel detected: upload every photo, draft the clothing fields from them,
  // and show the clothing review — all inside this dialog (no hand-off, which
  // was fragile across two dialogs).
  const startClothingReview = async (base64s: string[]) => {
    setHandingOff(true);
    try {
      const urls: string[] = [];
      for (const b64 of base64s) {
        try {
          const u = await uploadBase64(b64);
          if (u) urls.push(u);
        } catch {
          /* skip a failed image */
        }
      }
      setClothingUrls(urls);
      if (urls.length === 0) {
        setStep("review");
        return;
      }
      try {
        const res = await fetch("/api/ai/analyze-clothing", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ imageUrls: urls }),
        });
        if (res.ok) {
          const a = await res.json();
          if (a.name) setName(a.name);
          if (a.description) setDescription(a.description);
          if (a.suggestedPriceMin && a.suggestedPriceMax)
            setPrice(String(Math.round((Number(a.suggestedPriceMin) + Number(a.suggestedPriceMax)) / 2)));
          else if (a.suggestedPriceMax || a.suggestedPriceMin)
            setPrice(String(Number(a.suggestedPriceMax || a.suggestedPriceMin)));
          setClGarment(a.garmentType || "");
          setClColor(a.color || "");
          setClCondition(a.condition || "reworked");
          setClTags(Array.isArray(a.styleTags) ? a.styleTags : []);
        }
      } catch {
        /* draft failed — she fills it in herself */
      }
      setStep("clothing_review");
    } finally {
      setHandingOff(false);
    }
  };

  const switchToWeightBatch = async () => {
    if (!onSwitchToWeightBatch) return;
    const base64s = allImageBase64.length
      ? allImageBase64
      : imageBase64
        ? [imageBase64]
        : [];
    const urls: string[] = [];
    for (const b64 of base64s) {
      try {
        const u = await uploadBase64(b64);
        if (u) urls.push(u);
      } catch {
        /* skip a failed image */
      }
    }
    onSwitchToWeightBatch({ name, category, description, imageUrls: urls });
  };

  const addClothingPhotos = async (files: File[]) => {
    const list = files.filter((f) => f.type.startsWith("image/")).slice(0, 8);
    if (list.length === 0) return;
    setAddingClothingPhoto(true);
    try {
      const compressed = await Promise.all(list.map((f) => compressImage(f)));
      const urls: string[] = [];
      for (const c of compressed) {
        try {
          const u = await uploadBase64(c.base64);
          if (u) urls.push(u);
        } catch {
          /* skip */
        }
      }
      setClothingUrls((cur) => [...cur, ...urls].slice(0, 8));
    } finally {
      setAddingClothingPhoto(false);
    }
  };

  const saveClothing = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/items", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          category: CLOTHING_CATEGORY,
          subtype: clGarment || "other",
          price,
          qtyAvailable: 1,
          unit: "each",
          shortDescription: description,
          imageUrl: clothingUrls[0] || undefined,
          imagesJson: clothingUrls,
          status: "published",
          attributesJson: {
            size: clSize,
            condition: clCondition,
            color: clColor.trim() || undefined,
            vibes: clTags,
          },
        }),
      });
      if (!res.ok) {
        const raw = await res.text();
        let parsed: { message?: string } | null = null;
        try {
          parsed = JSON.parse(raw);
        } catch {}
        throw new Error(parsed?.message || raw || "Could not create listing");
      }
      return res.json();
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      toast({ title: "Listed!", description: `${name || "Your piece"} is live.` });
      onComplete(created);
    },
    onError: (e: any) => {
      toast({ title: "Couldn't publish", description: e?.message || "Please try again.", variant: "destructive" });
    },
  });

  const toggleClTag = (v: string) =>
    setClTags((cur) => (cur.includes(v) ? cur.filter((t) => t !== v) : [...cur, v]));

  const handleSaveClothing = () => {
    if (!name.trim()) {
      toast({ title: "Add a name", variant: "destructive" });
      return;
    }
    if (!(Number(price) > 0)) {
      toast({ title: "Add a price", variant: "destructive" });
      return;
    }
    if (!clSize) {
      toast({ title: "Pick a size", variant: "destructive" });
      return;
    }
    saveClothing.mutate();
  };

  // The draft this dialog is showing. Set when opened from the tray, or when a
  // photo is captured here. Everything below reads live status off the store so
  // an analysis that finishes while you're looking at it lands in the form.
  const [draftId, setDraftId] = useState<string | null>(openDraftId ?? null);
  const drafts = useComposerDrafts();
  const composer = drafts.find((d) => d.id === draftId) ?? null;
  const analyzing = composer?.status === "analyzing";
  const offlineQueued = composer?.status === "queued_offline";

  // Populate the review form from a finished analysis (food) or route to the
  // clothing flow. Shared by the live path and a reopened background draft.
  const applyAnalysis = (data: ComposerAnalysis) => {
    trackEvent("list_ai_succeeded", undefined, { kind: data.kind ?? "food" });
    // Apparel routes to the clothing flow (multiple views + clothing fields),
    // not the food review below. Upload the photo, then hand off.
    if (data.kind === "clothing") {
      void startClothingReview(
        allImageBase64.length ? allImageBase64 : imageBase64 ? [imageBase64] : [],
      );
      return;
    }
    setAnalysis(data as unknown as ProductAnalysis);
    setName(data.name);
    // A photo of a whole/live animal sets the listing up as a meat share
    // (sell the animal or a share of it, not packaged cuts).
    const wholeAnimal = data.isWholeAnimal === true;
    setIsMeatShare(wholeAnimal);
    if (wholeAnimal) setShareSize("quarter");
    setCategory(wholeAnimal ? "meat" : data.category);
    setDescription(data.description);
    setUnit(wholeAnimal ? shareUnit("quarter") : data.unit);
    // For a whole-animal share the AI price is for the whole animal, not one
    // share, so leave price blank and let the seller price a single share
    // using the hanging-weight hint instead of a misleading prefill.
    if (!wholeAnimal && data.suggestedPriceMin && data.suggestedPriceMax) {
      const avg = ((data.suggestedPriceMin + data.suggestedPriceMax) / 2).toFixed(2);
      setPrice(avg);
    } else if (!wholeAnimal && data.suggestedPriceMin) {
      setPrice(data.suggestedPriceMin.toFixed(2));
    }
    if (data.estimatedWeightLabel) {
      setWeight(data.estimatedWeightLabel);
    }
    setGrowthState(data.growthState ?? "ripe");
    if (data.growthState === "growing" && typeof data.daysUntilRipe === "number") {
      setDaysUntilRipe(String(data.daysUntilRipe));
    } else {
      setDaysUntilRipe("");
    }
    setStep("review");
  };

  // Land a finished analysis in the form, or drop into manual review on error
  // so the maker is never dead-ended on the photo screen. The draft is NOT
  // removed here — it stays in the tray, photo and all, until the listing is
  // actually submitted. Removing it on error was why a failed analysis reopened
  // as a blank form with no image.
  const appliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!composer || appliedRef.current === composer.id) return;
    if (composer.status === "ready" && composer.analysis) {
      appliedRef.current = composer.id;
      applyAnalysis(composer.analysis);
    } else if (composer.status === "error") {
      appliedRef.current = composer.id;
      trackEvent("list_ai_failed");
      toast({
        title: "Couldn't auto-fill from the photo",
        description: "No problem, add the details yourself below and you're good to go.",
      });
      setStep("review");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composer?.status, composer?.id]);

  // Opened from the tray: restore that draft's photo so the seller picks up
  // where they left off without re-shooting it.
  useEffect(() => {
    const d = getDraft(openDraftId);
    if (!d) return;
    setDraftId(d.id);
    setImageBase64(d.imageBase64);
    setImagePreview(`data:image/jpeg;base64,${d.imageBase64}`);
    setAllImageBase64(d.allImageBase64?.length ? d.allImageBase64 : [d.imageBase64]);
    if (d.status !== "ready" && d.status !== "error") setStep("photo");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDraftId]);

  // "Save to drafts" — park it on the server as a real draft the seller can
  // finish from the dashboard, and drop it from the local tray. Deliberately
  // minimal: only what's needed to identify the listing later, since the point
  // is to get it out of the way, not to publish it.
  const saveAsDraft = useMutation({
    mutationFn: async () => {
      let imageUrl = "";
      if (imageBase64) {
        try {
          const byteString = atob(imageBase64);
          const bytes = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
          const formData = new FormData();
          formData.append("image", new Blob([bytes], { type: "image/jpeg" }), "listing.jpg");
          const uploadRes = await fetch("/api/uploads/item-image", {
            method: "POST",
            credentials: "include",
            body: formData,
          });
          if (uploadRes.ok) imageUrl = (await uploadRes.json()).imageUrl || "";
        } catch {
          /* save the draft without the photo rather than lose the details */
        }
      }
      const res = await fetch("/api/items", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          description,
          price: price || "0",
          category,
          unit,
          quantity: Number(quantity) || 10,
          imageUrl: imageUrl || undefined,
          status: "draft",
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not save draft");
      return res.json();
    },
    onSuccess: () => {
      if (draftId) removeDraft(draftId);
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      toast({
        title: "Saved to drafts",
        description: "Finish it any time from Drafts on your dashboard.",
      });
      onCancel();
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't save draft", description: err.message, variant: "destructive" }),
  });

  const saveListing = useMutation({
    mutationFn: async () => {
      // Upload the COMPRESSED image (~1200px), not the original camera file —
      // a multi-MB phone photo throws "Failed to fetch" on mobile uploads and
      // killed the whole listing. Wrapped so an image hiccup never blocks the
      // listing; it just gets created without the photo.
      let imageUrl = "";
      if (imageBase64) {
        try {
          const byteString = atob(imageBase64);
          const bytes = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
          const blob = new Blob([bytes], { type: "image/jpeg" });
          const formData = new FormData();
          formData.append("image", blob, "listing.jpg");
          const uploadRes = await fetch("/api/uploads/item-image", {
            method: "POST",
            credentials: "include",
            body: formData,
          });
          if (uploadRes.ok) {
            const uploadData = await uploadRes.json();
            imageUrl = uploadData.imageUrl || "";
          }
        } catch {
          /* image upload failed (size/network) — create the listing without it */
        }
      }

      const bulkDiscountValid =
        bulkDiscountOn && Number(discountThreshold) > 0 && Number(discountPercent) > 0;
      const canGrow = ["produce", "garden", "flowers"].includes(category);
      const days =
        canGrow && growthState === "growing" ? Math.max(1, Number(daysUntilRipe) || 14) : null;
      const estimatedRipeDate =
        days != null
          ? new Date(Date.now() + days * 86_400_000).toISOString()
          : null;

      // Choosing a heat level in review tags the item a hot sauce; ignore a
      // stale level if the seller switched to a non-sauce category.
      const heatApplies =
        (category === "prepared" || category === "pantry") && heatLevel != null;

      // Then create the listing
      const res = await fetch("/api/items", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          description,
          price: price,
          category,
          subtype: heatApplies ? "hot-sauce" : analysis?.subtype || undefined,
          heatLevel: heatApplies ? heatLevel : undefined,
          unit,
          quantity: isMadeToOrder ? 9999 : Number(quantity) || 10,
          imageUrl: imageUrl || undefined,
          growthState: isMadeToOrder || isMeatShare || !canGrow ? "ripe" : growthState,
          estimatedRipeDate: isMadeToOrder || isMeatShare || !canGrow ? null : estimatedRipeDate,
          isMeatShare: isMeatShare ? 1 : 0,
          processingFeeCents: isMeatShare
            ? Math.round((Number(processingFee) || 0) * 100)
            : null,
          slaughterDate:
            isMeatShare && slaughterDate
              ? new Date(`${slaughterDate}T12:00:00`).toISOString()
              : null,
          isGlutenFree: glutenFreePerItem && glutenFree != null ? (glutenFree ? 1 : 0) : undefined,
          isMadeToOrder: isMadeToOrder ? 1 : 0,
          leadTimeDays: isMadeToOrder ? (Number(leadTimeDays) || null) : null,
          minQty: isMadeToOrder ? Math.max(1, Number(minQty) || 1) : 1,
          requiresDeposit: isMadeToOrder && requiresDeposit ? 1 : 0,
          depositPercent:
            isMadeToOrder && requiresDeposit
              ? Math.min(100, Math.max(1, Number(depositPercent) || 25))
              : null,
          bulkDiscountThresholdCents: bulkDiscountValid
            ? Math.round(Number(discountThreshold) * 100)
            : null,
          bulkDiscountPercent: bulkDiscountValid ? Math.round(Number(discountPercent)) : null,
          customFields: isMadeToOrder
            ? customFields
                .map((f) => ({
                  ...f,
                  label: f.label.trim(),
                  options: fieldNeedsOptions(f.type)
                    ? (f.options ?? []).map((o) => o.trim()).filter(Boolean)
                    : undefined,
                }))
                .filter((f) => f.label.length > 0)
            : undefined,
        }),
      });
      if (!res.ok) {
        const raw = await res.text();
        let parsed: { code?: string; message?: string } | null = null;
        try {
          parsed = JSON.parse(raw);
        } catch {}
        const err = new Error(parsed?.message || raw || "Could not create listing");
        (err as any).code = parsed?.code;
        throw err;
      }
      const created = await res.json();

      // Attach the chosen fulfillment options. Best-effort: the listing already
      // exists and the seller's active options still apply, so a failure here
      // shouldn't fail the whole publish.
      // Named price options. Best-effort for the same reason as fulfillment:
      // the listing already exists, and a single-price listing is a valid
      // fallback if this call fails.
      const extraRows = priceOptions
        .map((o) => ({
          label: o.label.trim(),
          priceCents: Math.round(parseFloat(o.price || "0") * 100),
          qty: parseInt(o.qty || "0", 10),
        }))
        .filter((o) => o.label && o.priceCents > 0 && o.qty > 0);
      // Options replace the single price, so the listing's own price/unit/qty
      // has to be one of them or it silently becomes unbuyable. Build it here
      // from the final field values rather than seeding a row earlier, which
      // captured whatever was typed at the moment the seller clicked add.
      const baseRow = {
        label: (unit || "each").trim(),
        priceCents: Math.round(parseFloat(price || "0") * 100),
        qty: parseInt(quantity || "0", 10) || 10,
      };
      const optionRows =
        extraRows.length > 0 && baseRow.priceCents > 0
          ? [
              baseRow,
              ...extraRows.filter(
                (o) => o.label.toLowerCase() !== baseRow.label.toLowerCase(),
              ),
            ]
          : extraRows;
      if (created?.id && optionRows.length > 0) {
        try {
          await fetch(`/api/items/${created.id}/price-options`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ options: optionRows }),
          });
        } catch {
          /* listing keeps its single price */
        }
      }

      if (created?.id && chosenFulfillmentIds.length > 0 && chosenFulfillmentIds.length !== activeProfiles.length) {
        try {
          await fetch(`/api/items/${created.id}/fulfillment-profiles`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ profileIds: chosenFulfillmentIds }),
          });
        } catch {
          /* keeps the seller's full set, which is the safe superset */
        }
      }
      return created;
    },
    onSuccess: (created: any) => {
      publishedRef.current = true;
      if (draftId) removeDraft(draftId);
      trackEvent("list_published", undefined, { madeToOrder: isMadeToOrder, needsPhoto: !!created?.needsPhoto });
      if (created?.needsPhoto) {
        toast({
          title: "Saved as a draft — add a photo to publish",
          description:
            "Listings need a photo to go public. Open it from your drafts, add a photo, and it'll go live.",
          variant: "destructive",
        });
      } else if (created?.fulfillmentAutoDefaulted) {
        toast({
          title: "Listed with pickup at your address",
          description:
            "We set pickup as the default so you're live fast. Add delivery or hub pickup anytime in Fulfillment.",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/seller/fulfillment-profiles"] });
      } else {
        toast({ title: "Listing created!", description: `${name} is now live.` });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      onComplete(created);
    },
    onError: (err: Error) => {
      trackEvent("list_failed", undefined, {
        code: (err as any).code || null,
        message: String(err.message || "").slice(0, 200),
      });
      if ((err as any).code === "seller_needs_address") {
        // Server says we don't have a geocoded address — drop the seller
        // into the inline fixer instead of showing a dead-end toast. All
        // listing form state stays in component state, so they pick up
        // where they left off when they return to "review".
        queryClient.invalidateQueries({ queryKey: ["/api/account"] });
        setAddrError(err.message);
        setStep("address");
        return;
      }
      toast({ title: "Could not create listing", description: err.message, variant: "destructive" });
      setStep("review");
    },
  });

  const compressImage = (file: File, maxWidth = 1200, quality = 0.8): Promise<{ preview: string; base64: string }> => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width;
          let h = img.height;
          if (w > maxWidth) {
            h = Math.round(h * (maxWidth / w));
            w = maxWidth;
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d")!;
          ctx.drawImage(img, 0, 0, w, h);
          const preview = canvas.toDataURL("image/jpeg", quality);
          const base64 = preview.split(",")[1];
          resolve({ preview, base64 });
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelected = async (file: File) => {
    setImageFile(file);
    const { preview, base64 } = await compressImage(file);
    setImagePreview(preview);
    setImageBase64(base64);
    setAllImageBase64([base64]);
    trackEvent("list_photo_captured");
    setDraftId(beginComposer(base64, [base64]));
  };

  // Multiple photos picked from the gallery (multiple views of one item). We
  // classify from the first; if it's apparel, all of them carry into the
  // clothing gallery. Food stays single-image (uses the first).
  const handleImagesSelected = async (files: File[]) => {
    const list = files.filter((f) => f.type.startsWith("image/")).slice(0, 8);
    if (list.length === 0) return;
    if (list.length === 1) {
      await handleImageSelected(list[0]);
      return;
    }
    const compressed = await Promise.all(list.map((f) => compressImage(f)));
    setImageFile(list[0]);
    setImagePreview(compressed[0].preview);
    setImageBase64(compressed[0].base64);
    setAllImageBase64(compressed.map((c) => c.base64));
    trackEvent("list_photo_captured");
    setDraftId(beginComposer(compressed[0].base64, compressed.map((c) => c.base64)));
  };

  if (step === "address") {
    return (
      <Card className="mx-auto w-full min-w-0 max-w-md">
        <CardContent className="pt-6">
          <SellerAddressFixer
            initialError={addrError}
            onSaved={() => {
              setAddrError(null);
              setStep("photo");
            }}
            onCancel={onCancel}
          />
        </CardContent>
      </Card>
    );
  }

  if (step === "photo") {
    return (
      <Card className="mx-auto w-full min-w-0 max-w-md">
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Instant Listing</h2>
            <p className="text-sm text-muted-foreground">
              Snap a photo of your item and we'll fill in the listing for you.
            </p>
          </div>

          {(analyzing || offlineQueued || handingOff) && imagePreview && (
            <div className="relative">
              <img src={imagePreview} alt="Product" className="w-full rounded-lg" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-lg bg-black/55 p-4 text-center">
                {offlineQueued ? (
                  <>
                    <WifiOff className="h-8 w-8 text-white" />
                    <p className="text-sm font-medium text-white">
                      You're offline. We saved your photo and will analyze it automatically when you're back in service.
                    </p>
                  </>
                ) : (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin text-white" />
                    <p className="text-sm font-medium text-white">
                      {handingOff ? "Setting up your clothing listing…" : "Analyzing your product…"}
                    </p>
                  </>
                )}
                {!handingOff && (
                  <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
                    Minimize — I'll finish later
                  </Button>
                )}
              </div>
            </div>
          )}

          {!analyzing && !offlineQueued && !handingOff && (
            <>
              <label className="mb-3 flex cursor-pointer items-start gap-2.5 rounded-lg border border-border bg-white p-2.5">
                <input
                  type="checkbox"
                  checked={isMadeToOrder}
                  onChange={(e) => setIsMadeToOrder(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-primary"
                />
                <span className="text-xs leading-5 text-gray-900">
                  <span className="font-semibold">Made to order / custom</span>
                  <span className="block text-[10px] text-muted-foreground">
                    For items you make per order (e.g. custom cookies). Lets you upload a photo of past work from your gallery.
                  </span>
                </span>
              </label>
              {isMadeToOrder ? (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-6 transition hover:border-primary/50 hover:bg-primary/10"
                  >
                    <Camera className="h-9 w-9 text-primary" />
                    <span className="text-sm font-medium text-primary">Take a photo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-6 transition hover:border-primary/50 hover:bg-primary/10"
                  >
                    <ImageIcon className="h-9 w-9 text-primary" />
                    <span className="text-sm font-medium text-primary">Upload from gallery</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex w-full flex-col items-center gap-3 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-8 transition hover:border-primary/50 hover:bg-primary/10"
                  >
                    <Camera className="h-10 w-10 text-primary" />
                    <span className="text-sm font-medium text-primary">Take a photo of your item</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-input bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Add photos from album
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  trackEvent("list_manual_skip");
                  setStep("review");
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-input bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
              >
                No photo handy? Start with the details
              </button>
              <p className="text-center text-[11px] text-muted-foreground">
                Your own photos of the actual item. Listing clothing? Pick a few angles — they'll all carry over.
              </p>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageSelected(file);
                  e.target.value = "";
                }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  if (files.length) handleImagesSelected(files);
                  e.target.value = "";
                }}
              />
              <p className="text-center text-[11px] text-muted-foreground">
                Tip: place a quarter next to your product for size reference
              </p>
            </>
          )}

          <Button variant="ghost" onClick={onCancel} className="w-full">
            Cancel
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === "clothing_review") {
    return (
      <Card className="mx-auto w-full min-w-0 max-w-md">
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Review your piece</h2>
          </div>

          <div>
            <Label className="mb-1.5 block text-sm">Photos</Label>
            <div className="flex flex-wrap gap-2">
              {clothingUrls.map((url, i) => (
                <div key={url} className="relative h-16 w-16 overflow-hidden rounded-md border">
                  <img src={url} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                  {i === 0 && (
                    <span className="absolute left-0.5 top-0.5 rounded bg-primary/90 px-1 text-[8px] font-semibold text-primary-foreground">
                      Cover
                    </span>
                  )}
                </div>
              ))}
              {clothingUrls.length < 8 && (
                <>
                  <button
                    type="button"
                    onClick={() => clothingCameraInputRef.current?.click()}
                    disabled={addingClothingPhoto}
                    className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed text-muted-foreground disabled:opacity-50"
                    aria-label="Take a photo"
                  >
                    {addingClothingPhoto ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Camera className="h-5 w-5" />
                        <span className="text-[9px]">Camera</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => clothingPhotoInputRef.current?.click()}
                    disabled={addingClothingPhoto}
                    className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed text-muted-foreground disabled:opacity-50"
                    aria-label="Add from album"
                  >
                    <ImageIcon className="h-5 w-5" />
                    <span className="text-[9px]">Album</span>
                  </button>
                </>
              )}
            </div>
            <input
              ref={clothingCameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                if (files.length) addClothingPhotos(files);
                e.target.value = "";
              }}
            />
            <input
              ref={clothingPhotoInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                if (files.length) addClothingPhotos(files);
                e.target.value = "";
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reworked Denim Jacket" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={clGarment} onValueChange={setClGarment}>
                <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  {GARMENT_TYPES.map((g) => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Size</Label>
              <Select value={clSize} onValueChange={setClSize}>
                <SelectTrigger><SelectValue placeholder="Pick a size" /></SelectTrigger>
                <SelectContent>
                  {CLOTHING_SIZES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Condition</Label>
              <Select value={clCondition} onValueChange={setClCondition}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLOTHING_CONDITIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Color</Label>
              <Input value={clColor} onChange={(e) => setClColor(e.target.value)} placeholder="e.g. washed black" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[70px]"
              placeholder="The print, the vibe, the fit."
            />
          </div>

          <div className="space-y-1.5">
            <Label>Price ($)</Label>
            <Input type="number" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
          </div>

          <div className="space-y-1.5">
            <Label>Style tags</Label>
            <div className="flex flex-wrap gap-1.5">
              {CLOTHING_VIBES.map((v) => {
                const on = clTags.includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleClTag(v)}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium capitalize ${
                      on ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"
                    }`}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="ghost" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSaveClothing} disabled={saveClothing.isPending} className="flex-1">
              {saveClothing.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Publishing…</>
              ) : (
                "Publish"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === "review") {
    return (
      <Card className="mx-auto w-full min-w-0 max-w-md">
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Review & adjust</h2>
            {analysis?.confidence && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                analysis.confidence === "high" ? "bg-green-100 text-green-700" :
                analysis.confidence === "medium" ? "bg-amber-100 text-amber-700" :
                "bg-red-100 text-red-700"
              }`}>
                {analysis.confidence} confidence
              </span>
            )}
          </div>

          {imagePreview && (
            <img src={imagePreview} alt="Product" className="h-32 w-full rounded-lg object-cover" />
          )}

          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Product name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <div className="flex flex-wrap gap-1">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => {
                      setCategory(cat.value);
                      setHeatLevel(null);
                    }}
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
                      category === cat.value
                        ? "bg-primary text-primary-foreground"
                        : "border border-border hover:border-primary/50"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-[80px]" />
            </div>

            {onSwitchToWeightBatch && (
              <Button
                type="button"
                variant="secondary"
                onClick={switchToWeightBatch}
                className="w-full h-auto justify-between whitespace-normal py-3 text-left leading-snug"
              >
                <span className="flex items-center gap-2">
                  <Scale className="h-4 w-4 shrink-0" />
                  Selling many of these by weight? Add all the weights
                </span>
                <ChevronRight className="h-4 w-4 shrink-0" />
              </Button>
            )}

            {glutenFreePerItem && (
              <div className="rounded-lg border p-3">
                <Label className="text-xs">Is this gluten-free?</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    onClick={() => setGlutenFree(true)}
                    className={`rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
                      glutenFree === true ? "bg-primary text-primary-foreground" : "border border-border bg-background"
                    }`}
                  >
                    Gluten-free
                  </button>
                  <button
                    type="button"
                    onClick={() => setGlutenFree(false)}
                    className={`rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
                      glutenFree === false ? "bg-primary text-primary-foreground" : "border border-border bg-background"
                    }`}
                  >
                    Not gluten-free
                  </button>
                </div>
              </div>
            )}

            {(category === "prepared" || category === "pantry") && (
              <div className="rounded-lg border p-3">
                <Label className="text-xs">Hot sauce? Rate the heat</Label>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  Optional. Picking a level marks this a hot sauce and shows the flames to buyers.
                </p>
                <div className="mt-2">
                  <HeatPicker value={heatLevel} onChange={setHeatLevel} />
                </div>
              </div>
            )}

            {category === "meat" && (
              <div className="rounded-lg border bg-rose-50/60 p-3 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isMeatShare}
                    onChange={(e) => {
                      setIsMeatShare(e.target.checked);
                      if (e.target.checked) setUnit(shareUnit(shareSize));
                    }}
                    className="mt-0.5"
                  />
                  <span className="text-xs font-semibold text-rose-900">
                    This is a share of a live animal (meat share)
                  </span>
                </label>
                {isMeatShare && (
                  <div className="space-y-2">
                    <p className="text-[10px] text-rose-800/80">
                      You're selling a live-animal share, not finished cuts. Pick the share size,
                      enter the price for ONE share in the Price field, and set shares available to
                      how many the animal yields. Buyers pay the share price in full now; the
                      processing fee auto-charges on the slaughter date.
                    </p>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-rose-900">Share size</Label>
                      <div className="flex flex-wrap gap-1">
                        {SHARE_SIZES.map((s) => (
                          <button
                            key={s.value}
                            type="button"
                            onClick={() => {
                              setShareSize(s.value);
                              setUnit(s.unit);
                            }}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
                              shareSize === s.value
                                ? "bg-rose-600 text-white"
                                : "border border-rose-300 text-rose-900"
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-rose-900">Processing fee per share ($)</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={processingFee}
                        onChange={(e) => setProcessingFee(e.target.value)}
                        placeholder="e.g. 95.00"
                        className="h-8 text-xs bg-white text-gray-900"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-rose-900">Slaughter date</Label>
                      <Input
                        type="date"
                        value={slaughterDate}
                        onChange={(e) => setSlaughterDate(e.target.value)}
                        className="h-8 text-xs bg-white text-gray-900"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isMadeToOrder && !isMeatShare && ["produce", "garden", "flowers"].includes(category) && (
            <div className="rounded-lg border bg-emerald-50/60 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <Sprout className="h-3.5 w-3.5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1 space-y-1">
                  <p className="text-[11px] font-semibold text-emerald-900">
                    How are you selling this?
                  </p>
                  {analysis?.growthState === "growing" && analysis?.ripenessNotes && (
                    <p className="text-[10px] text-emerald-800/80 italic">
                      {analysis.ripenessNotes}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <button
                  type="button"
                  onClick={() => setGrowthState("ripe")}
                  className={`rounded-lg px-2 py-1.5 text-left transition ${
                    growthState === "ripe"
                      ? "bg-primary text-primary-foreground"
                      : "border border-border bg-white"
                  }`}
                >
                  <span className="block text-[11px] font-semibold">Sell it now</span>
                  <span className={`block text-[9px] ${growthState === "ripe" ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    Produce, or a plant the buyer takes home
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGrowthState("growing");
                    if (!daysUntilRipe.trim()) {
                      setDaysUntilRipe(
                        analysis?.daysUntilRipe != null
                          ? String(analysis.daysUntilRipe)
                          : "14",
                      );
                    }
                  }}
                  className={`rounded-lg px-2 py-1.5 text-left transition ${
                    growthState === "growing"
                      ? "bg-emerald-600 text-white"
                      : "border border-border bg-white"
                  }`}
                >
                  <span className="block text-[11px] font-semibold">Reserve the harvest</span>
                  <span className={`block text-[9px] ${growthState === "growing" ? "text-white/80" : "text-muted-foreground"}`}>
                    Still growing — deposit now, deliver when ripe
                  </span>
                </button>
              </div>
              {growthState === "growing" && (
                <div className="space-y-1">
                  <Label className="text-[10px] text-emerald-900">
                    Ready to harvest in about (days)
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={180}
                    value={daysUntilRipe}
                    onChange={(e) => setDaysUntilRipe(e.target.value)}
                    placeholder="e.g. 14"
                    className="h-8 text-xs bg-white text-gray-900"
                  />
                  <p className="text-[10px] text-emerald-800/80">
                    You keep the plant and deliver the harvest when it's ready. Buyers pay a 25% deposit to reserve; the 75% balance auto-charges on the ready date. Post a weekly progress photo so buyers know the crop is on track.
                  </p>
                </div>
              )}
            </div>
            )}

            {isMadeToOrder && (
              <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">Made to order</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px]">Ready in (days)</Label>
                    <Input type="number" min={0} max={60} value={leadTimeDays} onChange={(e) => setLeadTimeDays(e.target.value)} placeholder="e.g. 3" className="h-8 text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Minimum order</Label>
                    <Input type="number" min={1} value={minQty} onChange={(e) => setMinQty(e.target.value)} placeholder="1" className="h-8 text-xs" />
                  </div>
                </div>
                <label className="flex cursor-pointer items-start gap-2 pt-0.5">
                  <input
                    type="checkbox"
                    checked={requiresDeposit}
                    onChange={(e) => setRequiresDeposit(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-primary"
                  />
                  <span className="text-[11px] leading-4 text-gray-900">
                    <span className="font-semibold">Require a deposit</span>
                    <span className="block text-[10px] text-muted-foreground">
                      Buyer pays your chosen deposit now; the rest auto-charges on the ready date. Leave off to charge the full price up front.
                    </span>
                  </span>
                </label>
                {requiresDeposit && (
                  <div className="flex items-center gap-2 pl-6">
                    <Label className="text-[11px]">Deposit</Label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={depositPercent}
                      onChange={(e) => setDepositPercent(e.target.value)}
                      className="h-8 w-16 text-xs"
                    />
                    <span className="text-[10px] text-muted-foreground">% charged now</span>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">
                  {requiresDeposit
                    ? `Buyer pays a ${Math.min(100, Math.max(1, Number(depositPercent) || 25))}% deposit now; you confirm details in the order chat.`
                    : "Buyer pays your price now; you confirm details in the order chat."}
                </p>

                <div className="space-y-2 border-t border-primary/15 pt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px]">Buyer fills out</Label>
                    <button
                      type="button"
                      onClick={addField}
                      className="flex items-center gap-0.5 text-[10px] font-semibold text-primary"
                    >
                      <Plus className="h-3 w-3" /> Add question
                    </button>
                  </div>
                  {customFields.length === 0 && (
                    <p className="text-[10px] text-muted-foreground">
                      Optional. Ask what you need to make it — e.g. Flavor, Occasion, Color. Buyers answer when they order.
                    </p>
                  )}
                  {customFields.map((f) => (
                    <div key={f.id} className="space-y-1.5 rounded-md border border-card-border bg-background p-2">
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={f.label}
                          onChange={(e) => patchField(f.id, { label: e.target.value })}
                          placeholder="Question (e.g. Flavor)"
                          className="h-7 flex-1 text-xs"
                        />
                        <select
                          value={f.type}
                          onChange={(e) => {
                            const type = e.target.value as MadeToOrderField["type"];
                            patchField(f.id, {
                              type,
                              options: fieldNeedsOptions(type) ? (f.options ?? ["", ""]) : undefined,
                            });
                          }}
                          className="h-7 rounded-md border border-input bg-background px-1 text-[11px]"
                        >
                          <option value="text">Short text</option>
                          <option value="paragraph">Paragraph</option>
                          <option value="select">Dropdown</option>
                          <option value="checkbox">Checkboxes</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => removeField(f.id)}
                          aria-label="Remove question"
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {fieldNeedsOptions(f.type) && (
                        <div className="space-y-1 pl-1">
                          {(f.options ?? []).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-1">
                              <Input
                                value={opt}
                                onChange={(e) => patchOption(f.id, oi, e.target.value)}
                                placeholder={`Option ${oi + 1}`}
                                className="h-6 flex-1 text-[11px]"
                              />
                              <button
                                type="button"
                                onClick={() => removeOption(f.id, oi)}
                                aria-label="Remove option"
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addOption(f.id)}
                            className="flex items-center gap-0.5 text-[10px] font-semibold text-primary"
                          >
                            <Plus className="h-3 w-3" /> Add option
                          </button>
                        </div>
                      )}
                      <label className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={f.required}
                          onChange={(e) => patchField(f.id, { required: e.target.checked })}
                          className="h-3.5 w-3.5 accent-primary"
                        />
                        <span className="text-[10px] text-gray-900">Required</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* min-w-0 on each column: grid tracks default to minmax(auto, 1fr),
                and `auto` refuses to shrink below the content's min-content
                width — which for a number input is a browser-default intrinsic
                size. Three of those plus gaps overflowed the dialog and made it
                scroll sideways. */}
            <div className="grid grid-cols-3 gap-2">
              <div className="min-w-0 space-y-1">
                <Label className="text-xs">{isMeatShare ? "Price / share ($)" : "Price ($)"}</Label>
                <Input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} />
              </div>
              <div className="min-w-0 space-y-1">
                <Label className="text-xs">Unit</Label>
                <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="per lb" />
              </div>
              <div className="min-w-0 space-y-1">
                <Label className="text-xs">{isMeatShare ? "Shares avail." : "Qty available"}</Label>
                <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              </div>
            </div>
            {!isMeatShare && (
              <div className="space-y-2">
                {/* Same column order as the Price / Unit / Qty row above — an
                    option row IS another price/unit/qty triple ("Per dozen" is
                    a unit), so the headers up there label these too and none
                    are repeated. */}
                {priceOptions.map((opt, i) => (
                  <div key={i} className="grid grid-cols-3 items-end gap-2">
                    <div className="min-w-0">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="20.00"
                        value={opt.price}
                        onChange={(e) =>
                          setPriceOptions((cur) =>
                            cur.map((o, j) => (j === i ? { ...o, price: e.target.value } : o)),
                          )
                        }
                      />
                    </div>
                    <div className="min-w-0">
                      <Input
                        value={opt.label}
                        placeholder="per dozen"
                        onChange={(e) =>
                          setPriceOptions((cur) =>
                            cur.map((o, j) => (j === i ? { ...o, label: e.target.value } : o)),
                          )
                        }
                      />
                    </div>
                    {/* Remove button overlays the input's right edge so this
                        input stays the same width as the two beside it. */}
                    <div className="relative min-w-0">
                      <Input
                        type="number"
                        min="1"
                        max={60}
                        className="min-w-0 pr-7"
                        value={opt.qty}
                        onChange={(e) =>
                          setPriceOptions((cur) =>
                            cur.map((o, j) => (j === i ? { ...o, qty: e.target.value } : o)),
                          )
                        }
                      />
                      <button
                        type="button"
                        aria-label="Remove option"
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                        onClick={() => setPriceOptions((cur) => cur.filter((_, j) => j !== i))}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setPriceOptions((cur) => [...cur, { label: "", price: "", qty: quantity || "10" }])
                  }
                  className="flex items-center gap-0.5 text-[11px] font-semibold text-primary"
                  data-testid="button-add-price-option"
                >
                  <Plus className="h-3 w-3" /> Add another price option
                </button>
                {priceOptions.length > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    Buyers choose between your price above and these. Up to 60 of each.
                  </p>
                )}
              </div>
            )}

            {isMeatShare && (
              <div className="space-y-1 rounded-md bg-rose-50/60 p-2">
                <p className="text-[11px] text-rose-800/80">
                  Price is for ONE{" "}
                  {SHARE_SIZES.find((s) => s.value === shareSize)?.label.toLowerCase()} share. Shares
                  available = how many you're selling (a pig is usually 2 halves, 4 quarters, or 8
                  eighths).
                </p>
                {analysis?.pricingBasis && (
                  <p className="text-[11px] italic text-rose-900/70">{analysis.pricingBasis}</p>
                )}
              </div>
            )}

            {analysis?.suggestedPriceMin && analysis?.suggestedPriceMax && !isMeatShare && (
              <p className="text-[11px] text-muted-foreground">
                Suggested: ${analysis.suggestedPriceMin.toFixed(2)} – ${analysis.suggestedPriceMax.toFixed(2)} {unit}
              </p>
            )}

            {analysis?.pricingBasis && !isMeatShare && (
              <p className="text-[10px] text-muted-foreground/70 italic">
                {analysis.pricingBasis}
              </p>
            )}

            {(analysis?.estimatedSize || analysis?.estimatedWeightLabel) && (
              <div className="rounded-lg border bg-blue-50 p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="space-y-1 flex-1">
                    {analysis?.estimatedSize && (
                      <p className="text-[11px] text-muted-foreground">
                        Size estimate: {analysis.estimatedSize}
                      </p>
                    )}
                    {analysis?.estimatedWeightLabel && (
                      <p className="text-[11px] text-blue-800 font-medium">
                        Estimated weight: {analysis.estimatedWeightLabel}
                      </p>
                    )}
                    <p className="text-[10px] text-blue-600/80">
                      This is an estimate based on typical product weight. If it seems off, weigh your product and enter the actual weight below.
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-blue-700">Weight (override if needed)</Label>
                  <Input
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="e.g. 8 oz, 1.2 lb"
                    className="h-8 text-xs bg-white text-gray-900"
                  />
                </div>
              </div>
            )}
          </div>

          {/* One section either way, so a seller with nothing set up can still
              add an option here instead of being told to go elsewhere — that's
              the seller who most needs it. */}
          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5 text-primary" />
              <p className="text-[11px] font-semibold text-foreground">How buyers get this</p>
            </div>
            {activeProfiles.length === 0 ? (
              <p className="text-[10px] text-muted-foreground">
                No options set up yet — we'll default to pickup at your address so you can get
                listed fast. Add a specific one below if you'd rather.
              </p>
            ) : (
              activeProfiles.map((profile) => {
                const checked = chosenFulfillmentIds.includes(profile.id);
                return (
                  <label key={profile.id} className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setSelectedFulfillmentIds(
                          e.target.checked
                            ? [...chosenFulfillmentIds, profile.id]
                            : chosenFulfillmentIds.filter((id) => id !== profile.id),
                        )
                      }
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      data-testid={`quickadd-fulfillment-${profile.id}`}
                    />
                    {/* min-w-0 + break-words: a seller-typed profile name can be
                        long or a single unbroken string, which would otherwise
                        stretch this flex row and push the dialog wider. */}
                    <span className="min-w-0 break-words text-xs leading-5 text-foreground">
                      {profile.name}
                    </span>
                  </label>
                );
              })
            )}
            {activeProfiles.length > 0 && chosenFulfillmentIds.length === 0 && (
              <p className="text-[10px] font-medium text-destructive">
                Pick at least one, or buyers won't have a way to get it.
              </p>
            )}

              {addingFulfillment ? (
                <div className="space-y-2 rounded-md border bg-background p-2">
                  <Input
                    value={newFulfillmentName}
                    onChange={(e) => setNewFulfillmentName(e.target.value)}
                    placeholder="Name it, e.g. Saturday farm stand"
                    className="h-8 text-xs"
                    maxLength={150}
                    data-testid="input-new-fulfillment-name"
                  />
                  <div className="flex gap-1.5">
                    {(
                      [
                        ["seller_pickup", "Pickup"],
                        ["local_delivery", "Delivery"],
                        ["drop_zone_pickup", "Hub"],
                      ] as const
                    ).map(([method, label]) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setNewFulfillmentMethod(method)}
                        className={`h-7 min-w-0 flex-1 rounded-md border text-[11px] font-medium ${newFulfillmentMethod === method ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background text-foreground"}`}
                        data-testid={`button-new-fulfillment-${method}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {newFulfillmentMethod === "drop_zone_pickup" && (
                    <select
                      value={newFulfillmentZoneId}
                      onChange={(e) => setNewFulfillmentZoneId(e.target.value)}
                      className="h-8 w-full min-w-0 rounded-md border border-input bg-background px-2 text-xs"
                      data-testid="select-new-fulfillment-zone"
                    >
                      <option value="">Choose a community hub…</option>
                      {dropZones.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.city ? `${z.name} — ${z.city}` : z.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <div className="flex gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => createFulfillment.mutate()}
                      disabled={
                        !newFulfillmentName.trim() ||
                        createFulfillment.isPending ||
                        (newFulfillmentMethod === "drop_zone_pickup" && !newFulfillmentZoneId)
                      }
                      className="h-7 flex-1 text-xs"
                      data-testid="button-save-new-fulfillment"
                    >
                      {createFulfillment.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : null}
                      Add option
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAddingFulfillment(false);
                        setNewFulfillmentName("");
                      }}
                      className="h-7 text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                  {/* Optional window. No days chosen = no time slots, which is
                      the same "anytime" behaviour the dashboard's creator has,
                      so adding an option stays a two-field job when you don't
                      care about hours. */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-medium text-muted-foreground">
                      Days (optional — leave blank for anytime)
                    </p>
                    <div className="flex gap-1">
                      {QUICK_DAYS.map(({ label, value }) => {
                        const on = newFulfillmentDays.includes(value);
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() =>
                              setNewFulfillmentDays(
                                on
                                  ? newFulfillmentDays.filter((d) => d !== value)
                                  : [...newFulfillmentDays, value],
                              )
                            }
                            className={`h-7 min-w-0 flex-1 rounded-md border text-[10px] font-medium ${on ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background text-foreground"}`}
                            data-testid={`button-new-fulfillment-day-${value}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {newFulfillmentDays.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="time"
                          value={newFulfillmentStart}
                          onChange={(e) => setNewFulfillmentStart(e.target.value)}
                          className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs"
                          data-testid="input-new-fulfillment-start"
                        />
                        <span className="shrink-0 text-[10px] text-muted-foreground">to</span>
                        <input
                          type="time"
                          value={newFulfillmentEnd}
                          onChange={(e) => setNewFulfillmentEnd(e.target.value)}
                          className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs"
                          data-testid="input-new-fulfillment-end"
                        />
                      </div>
                    )}
                  </div>

                  {newFulfillmentMethod === "drop_zone_pickup" && dropZones.length === 0 && (
                    <p className="text-[10px] text-muted-foreground">
                      No community hubs near you yet. You can set one up in Seller Dashboard →
                      Fulfillment.
                    </p>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingFulfillment(true)}
                  className="flex items-center gap-1 pt-0.5 text-[11px] font-medium text-primary"
                  data-testid="button-add-fulfillment"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {activeProfiles.length === 0 ? "Add a pickup or delivery option" : "Add another way"}
                </button>
              )}
          </div>

          <div className="rounded-lg border p-3 space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bulkDiscountOn}
                onChange={(e) => setBulkDiscountOn(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs font-semibold text-foreground">Add a bulk discount</span>
            </label>
            {bulkDiscountOn && (
              <div className="space-y-2">
                <p className="text-[10px] text-muted-foreground">
                  Once a buyer's order from you reaches the amount below, they get the % off your
                  items. The discount comes out of your earnings.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px]">Spend at least ($)</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      value={discountThreshold}
                      onChange={(e) => setDiscountThreshold(e.target.value)}
                      placeholder="e.g. 50"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Discount (%)</Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={90}
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(e.target.value)}
                      placeholder="e.g. 10"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
                {Number(discountThreshold) > 0 && Number(discountPercent) > 0 && (
                  <p className="text-[10px] font-medium text-primary">
                    Buyers who spend ${Number(discountThreshold).toFixed(2)}+ save{" "}
                    {Math.round(Number(discountPercent))}%.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Leaving without publishing: Minimize keeps it in the tray, Save
              draft moves it to the dashboard's Drafts tab, Discard throws it
              away — without these the only way to clear a bar was to publish
              it. Laid out as icon-over-caption thirds rather than three
              labelled buttons in a row, because "Save to drafts" pushed that
              row wider than the panel on a phone and wrapping it looks broken.
              Mirrors the bottom nav's icon+caption pattern. */}
          {draftId && (
            <div className="grid grid-cols-3 gap-1 rounded-lg border bg-muted/30 p-1">
              <button
                type="button"
                onClick={onCancel}
                className="flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-md py-2 text-[10px] font-medium text-muted-foreground hover:bg-muted active:scale-95"
                data-testid="button-minimize-listing"
              >
                <Minus className="h-4 w-4" />
                Minimize
              </button>
              <button
                type="button"
                onClick={() => saveAsDraft.mutate()}
                disabled={saveAsDraft.isPending || !name.trim()}
                className="flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-md py-2 text-[10px] font-medium text-muted-foreground hover:bg-muted active:scale-95 disabled:opacity-40"
                data-testid="button-save-draft-listing"
              >
                {saveAsDraft.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Archive className="h-4 w-4" />
                )}
                Save draft
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm("Discard this listing? The photo and details will be deleted.")) return;
                  removeDraft(draftId);
                  toast({ title: "Listing discarded" });
                  onCancel();
                }}
                className="flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-md py-2 text-[10px] font-medium text-destructive hover:bg-destructive/10 active:scale-95"
                data-testid="button-discard-listing"
              >
                <Trash2 className="h-4 w-4" />
                Discard
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="ghost" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!unit.trim()) {
                  trackEvent("list_submit_blocked", undefined, { reason: "missing_unit" });
                  toast({
                    title: "Add a unit",
                    description: "Tell buyers what they get for the price (e.g. per dozen, each, per lb).",
                  });
                  return;
                }
                // Only the photo analysis sets category, so the "start with the
                // details" entry point reaches here with it still empty and the
                // chips showing nothing selected. Unset used to be silently
                // filed as produce, which is how a candle became a vegetable.
                if (!category) {
                  trackEvent("list_submit_blocked", undefined, { reason: "missing_category" });
                  toast({
                    title: "Pick a category",
                    description: "Tap the one that fits so buyers can find this in the right place.",
                  });
                  return;
                }
                trackEvent("list_submit_tapped");
                setStep("saving");
                saveListing.mutate();
              }}
              disabled={!name.trim() || !price || saveListing.isPending}
              className="flex-1"
            >
              {saveListing.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Create listing
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // saving state
  return (
    <Card className="mx-auto w-full min-w-0 max-w-md">
      <CardContent className="flex flex-col items-center gap-3 py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium">Creating your listing...</p>
      </CardContent>
    </Card>
  );
}
